import type { CSSProperties, FC, ReactNode } from 'react'
import type {
    Background,
    Design,
    DesignElement,
    Pattern,
    TextElement
} from '@/lib/design/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { getFont, nearestWeight } from '@/lib/design/fonts'
import { imageUrl } from '@/lib/design/images'
import { resolveLiveText } from '@/lib/design/layouts'
import { getSize } from '@/lib/design/sizes'
import { parseRuns } from '@/lib/design/text'

interface SlidePreviewProps {
    design: Design
    index: number
    width: number
    className?: string
    label?: string
    fluid?: boolean
}

const backgroundStyle = (background: Background): CSSProperties => {
    if (background.type === 'solid') return { backgroundColor: background.color }
    if (background.type === 'gradient') return { backgroundImage: `linear-gradient(${background.angle}deg, ${background.from}, ${background.to})` }

    const url = imageUrl(background.image)

    return {
        backgroundColor: background.fallback,
        backgroundImage: url ? `url("${url}")` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
    }
}

const patternStyle = (pattern: Pattern): CSSProperties | null => {
    if (pattern.type === 'none' || pattern.opacity <= 0) return null

    const color = pattern.color

    switch (pattern.type) {
        case 'dots': return { opacity: pattern.opacity, backgroundImage: `radial-gradient(${color} 3px, transparent 3.5px)`, backgroundSize: '40px 40px', backgroundPosition: '0 0' }
        case 'grid': return { opacity: pattern.opacity, backgroundImage: `linear-gradient(${color} 2px, transparent 2px), linear-gradient(90deg, ${color} 2px, transparent 2px)`, backgroundSize: '60px 60px', backgroundPosition: '-1px -1px' }
        case 'lines': return { opacity: pattern.opacity, backgroundImage: `repeating-linear-gradient(transparent 0 63px, ${color} 63px 65px)` }
        case 'notebook': return { opacity: pattern.opacity, top: 136, backgroundImage: `repeating-linear-gradient(transparent 0 63px, ${color} 63px 65px)` }
        default: return null
    }
}

const textStyle = (element: TextElement): CSSProperties => ({
    fontFamily: getFont(element.font).family,
    fontSize: element.size,
    fontWeight: nearestWeight(element.font, element.weight),
    fontStyle: element.italic ? 'italic' : 'normal',
    lineHeight: element.lineHeight,
    letterSpacing: element.letterSpacing,
    textAlign: element.align,
    textTransform: element.uppercase ? 'uppercase' : 'none',
    color: element.color,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere'
})

const renderText = (element: TextElement, text: string): ReactNode => parseRuns(text).map((run, index) => {
    if (!run.highlight) return <span key={index}>{run.text}</span>

    if (element.highlightStyle === 'marker') {
        return (
            <span
                key={index}
                style={{
                    backgroundColor: element.highlight,
                    color: element.highlightText,
                    borderRadius: element.size * 0.14,
                    padding: `0 ${element.size * 0.12}px`,
                    boxDecorationBreak: 'clone',
                    WebkitBoxDecorationBreak: 'clone'
                }}
            >
                {run.text}
            </span>
        )
    }

    return <span key={index} style={{ color: element.highlight }}>{run.text}</span>
})

const ArrowShape: FC<{ color: string, width: number, height: number, strokeWidth: number }> = ({ color, width, height, strokeWidth }): ReactNode => {
    const head = Math.min(height / 2, width / 2)

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill='none' aria-hidden='true'>
            <path d={`M0 ${height / 2} H${width} M${width - head} ${height / 2 - head} L${width} ${height / 2} L${width - head} ${height / 2 + head}`} stroke={color} strokeWidth={strokeWidth} strokeLinecap='round' strokeLinejoin='round' />
        </svg>
    )
}

const renderElement = (element: DesignElement, design: Design, index: number): ReactNode => {
    const total = design.slides.length

    if (element.hiddenOnLast && index === total - 1) return null

    const base: CSSProperties = {
        position: 'absolute',
        left: element.x,
        top: element.y,
        opacity: element.opacity,
        transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
        transformOrigin: 'top left'
    }

    if (element.type === 'text') {
        if (element.role === 'counter' && total === 1) return null

        const text = resolveLiveText(element, index, total, design.identity)

        if (!text) return null

        return (
            <div key={element.id} style={{ ...base, width: element.width, ...textStyle(element) }}>
                {renderText(element, text)}
            </div>
        )
    }

    if (element.type === 'image') {
        const url = imageUrl(element.image)

        return (
            <div key={element.id} style={{ ...base, width: element.width, height: element.height, borderRadius: element.radius, overflow: 'hidden', backgroundColor: 'rgba(127,127,127,0.18)' }}>
                {url && <img src={url} alt='' style={{ width: '100%', height: '100%', objectFit: element.fit }} />}
            </div>
        )
    }

    const stroke = element.stroke && element.stroke !== 'transparent' && element.strokeWidth > 0

    if (element.shape === 'arrow' || element.shape === 'line') {
        const color = stroke ? element.stroke : element.fill

        if (element.shape === 'line') {
            return <div key={element.id} style={{ ...base, top: element.y + element.height / 2 - (element.strokeWidth || 4) / 2, width: element.width, height: element.strokeWidth || 4, backgroundColor: color, borderRadius: 99 }} />
        }

        return (
            <div key={element.id} style={base}>
                <ArrowShape color={color} width={element.width} height={element.height} strokeWidth={element.strokeWidth || 4} />
            </div>
        )
    }

    return (
        <div
            key={element.id}
            style={{
                ...base,
                width: element.width,
                height: element.height,
                backgroundColor: element.fill !== 'transparent' ? element.fill : undefined,
                border: stroke ? `${element.strokeWidth}px solid ${element.stroke}` : undefined,
                borderRadius: element.shape === 'circle' ? '50%' : element.radius,
                boxShadow: element.shadow ? '0 16px 48px rgba(15, 20, 25, 0.14)' : undefined,
                boxSizing: 'border-box'
            }}
        />
    )
}

// A lightweight HTML version of a slide, for previews that do not need pixel-exact output.
export const SlidePreview: FC<SlidePreviewProps> = ({ design, index, width, className, label, fluid = false }): ReactNode => {
    const slide = design.slides[index]
    const size = getSize(design.aspect)
    const ref = useRef<HTMLDivElement>(null)
    const [measured, setMeasured] = useState<number | null>(null)
    const scale = (fluid && measured ? measured : width) / size.width
    const pattern = slide ? patternStyle(slide.pattern) : null

    // Fluid previews follow the width of their container once the page is in the browser.
    useEffect(() => {
        if (!fluid || !ref.current || typeof ResizeObserver === 'undefined') return

        const element = ref.current
        const observer = new ResizeObserver(entries => {
            const next = entries[0]?.contentRect.width

            if (next) setMeasured(next)
        })

        observer.observe(element)

        return () => observer.disconnect()
    }, [fluid])

    return (
        <div
            ref={ref}
            className={cn('relative overflow-hidden', className)}
            style={fluid ? { width: '100%', aspectRatio: `${size.width} / ${size.height}` } : { width, height: size.height * scale }}
            role='img'
            aria-label={label || 'Slide preview'}
        >
            {slide && (
                <div
                    className='absolute left-0 top-0 origin-top-left'
                    style={{ width: size.width, height: size.height, transform: `scale(${scale})`, ...backgroundStyle(slide.background) }}
                >
                    {slide.background.type === 'image' && slide.background.overlayOpacity > 0 && (
                        <div className='absolute inset-0' style={{ backgroundColor: slide.background.overlay, opacity: slide.background.overlayOpacity }} />
                    )}

                    {pattern && <div className='absolute inset-0' style={pattern} />}

                    {slide.pattern.type === 'notebook' && (
                        <div className='absolute top-0 bottom-0' style={{ left: 128, width: 3, backgroundColor: '#F87171', opacity: 0.75 }} />
                    )}

                    {slide.elements.map(element => renderElement(element, design, index))}
                </div>
            )}
        </div>
    )
}
